import React, { useMemo } from 'react';
import { useGetAllWorkletsForParentSpecQuery } from '@frontend/app/hooks';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { ClientFeature } from '@frontend/app/constants';
import { WorkflowStagesSection } from '../sections/WorkflowStagesSection';
import { useInitializeCampaign } from '../hooks/useInitializeCampaign';
import { TProject, TWorklet } from '../../types';
import { SaveCampaignCTA } from '../components/SaveCampaignCTA';
import { SidebarLayout } from '../../SettingsPage/components/SidebarLayout';
import { CreatorPortalPreview } from '../components/CreatorPortalPreview';

interface Props {
  mode?: 'add' | 'edit';
  project?: TProject;
  onSave?: () => void;
  refetchProjects?: () => void;
}

export const WorkflowTab: React.FC<Props> = ({
  mode = 'edit',
  project,
  onSave,
  refetchProjects,
}) => {
  const {
    data: {
      worklets: defaultWorklets = undefined,
    } = {},
    loading: isWorkletsLoading,
    refetch: refetchWorklets,
  } = useGetAllWorkletsForParentSpecQuery({
    variables: {
      specKey: project?.specKey,
    },
    skip: !project?.specKey,
    notifyOnNetworkStatusChange: true,
  });

  // Initialize campaign data
  useInitializeCampaign(mode, project);

  const { [ClientFeature.NEW_FLEX_ACTIVATION]: isNewFlexActivationEnabled } = useClientFeatures();

  const sections = useMemo(() => {
    return [
      {
        id: 'workflow-stages',
        label: 'Workflow Stages',
        component: (
          <div className="flex justify-between">
            <div className="flex flex-col gap-4">
              <SaveCampaignCTA
                mode={mode}
                project={project}
                onSave={() => {
                  onSave?.();
                  refetchWorklets?.();
                }}
                refetchProjects={refetchProjects}
                title="Workflow"
              />
              <WorkflowStagesSection
                defaultWorklets={defaultWorklets as TWorklet[]}
                parentSpecKey={project?.specKey}
                projectId={project?.id}
                showFlexActivationUI
              />
            </div>
          </div>
        ),
        rightSection: (
          <div className="hidden lg:block lg:w-[465px] lg:min-w-[465px] border-l border-grey-3 h-full overflow-y-auto">
            <CreatorPortalPreview project={project} stages={defaultWorklets as TWorklet[]} isLoading={isWorkletsLoading} />
          </div>
        ),
      },
    ];
  }, [mode, project, onSave, refetchProjects, refetchWorklets, defaultWorklets, isWorkletsLoading]);

  if (isNewFlexActivationEnabled) {
    return (
      <SidebarLayout
        sections={sections}
        activeSectionId="workflow-stages"
        onlyShowActiveSection
      />
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-[800px] mx-auto">
        <SaveCampaignCTA
          mode={mode}
          project={project}
          onSave={onSave}
          refetchProjects={refetchProjects}
          title="Workflow Configuration"
        />
        <div className="mt-6">
          <WorkflowStagesSection
            defaultWorklets={defaultWorklets as TWorklet[]}
            parentSpecKey={project?.specKey}
            projectId={project?.id}
          />
        </div>
      </div>
    </div>
  );
};
